/**
 * Wave 11.4 — Separable 1D Gaussian fragment shaders.
 *
 * Two passes (horizontal + vertical) implement a full 2D Gaussian via the
 * separability of G(x, y) = G(x) · G(y). On a CPU this turns 2D convolution
 * O(N · K²) into 2D O(N · K); on a GPU the win is the same shape but
 * dominated by texture-cache reuse — the per-row read pattern hits the
 * texel cache linearly.
 *
 * Numerical contract (matches CPU `capture-sharpen.js::gaussianKernel1D` +
 * `applyGaussian1D`):
 *
 *   • Kernel radius r = ceil(3·σ) (same Wave 3 convention).
 *   • Kernel weights unit-sum (we normalise on upload host-side, NOT in
 *     the shader, so per-pixel cost is K MADs not K MADs + a divide).
 *   • Boundary: mirror-extend via `mirrorIndex(i, n)` — period 2(n-1),
 *     identical to CPU `_util.js::mirrorIndex`.
 *   • Alpha channel is smoothed alongside RGB — same as CPU. The
 *     editor's canonical buffer is α=1.0 so the difference is
 *     unobservable downstream, but parity stays clean for tests.
 *
 * Uniform layout (host-side packing):
 *
 *   uSrc       — RGBA32F input texture
 *   uSrcSize   — (w, h) ivec2 (because gl_FragCoord rounding on macOS WebGL2
 *                can drift; texelFetch + integer coords is unambiguous)
 *   uWeights   — float[KMAX+1]  symmetric half-kernel: w[0] is centre,
 *                w[1..r] is the half-spread (so the full kernel has
 *                2·r+1 taps reading w[|j|]).
 *                KMAX is the compile-time max half-length; r is uploaded
 *                via uRadius and the loop bails at j > uRadius.
 *   uRadius    — int half-length r ∈ [1, KMAX].
 *   uAxis      — 0 → horizontal, 1 → vertical. One shader serves both
 *                directions; avoids duplicating 60 lines of GLSL.
 *
 * KMAX is set to 200 because the slider caps σ at 200 and r = ceil(3·σ)
 * could in principle reach 600 — but for σ > 50 the caller is expected
 * to route to the repeated-box-filter approximation (CPU side, no GPU
 * needed). 200 is a generous safety margin for direct GPU sampling at
 * σ ≤ ~66 and keeps the uniform array small enough to fit in the
 * smallest WebGL2 uniform-storage budget (1024 float defaults).
 *
 * References:
 *   • Burt & Adelson (1983) "The Laplacian Pyramid as a Compact Image
 *     Code" — separable Gaussian.
 *   • Wolberg (1990) "Digital Image Warping" §3.3 — mirror-extend.
 */

/* Compile-time max half-kernel length. See header for rationale. */
export const KMAX = 200;

export const FRAG_GAUSSIAN_SEPARABLE = /* glsl */ `#version 300 es
precision highp float;
precision highp int;

uniform highp sampler2D uSrc;
uniform ivec2 uSrcSize;
uniform float uWeights[${KMAX + 1}];
uniform int   uRadius;
uniform int   uAxis;     // 0 = horizontal, 1 = vertical

in vec2 vUv;
out vec4 outColor;

/* Mirror-extend index — single modulo into period 2(n-1) so the
 * result is correct for ARBITRARILY-far-out indices (matches CPU
 * _util.js::mirrorIndex exactly, including the small-dim case
 * w=2, sigma=10 etc. where a fixed-iteration fold would underflow).
 *
 * GLSL "%" is implementation-defined for negative operands, so we
 * normalise into [0, period) by adding multiples of period before
 * the modulo. */
int mirrorIdx(int x, int n) {
    if (n <= 1) return 0;
    int period = 2 * (n - 1);
    int m = x % period;
    if (m < 0) m += period;
    return m < n ? m : period - m;
}

void main() {
    /* Pixel-perfect integer coords. */
    int xc = int(floor(vUv.x * float(uSrcSize.x)));
    int yc = int(floor(vUv.y * float(uSrcSize.y)));
    if (xc >= uSrcSize.x) xc = uSrcSize.x - 1;
    if (yc >= uSrcSize.y) yc = uSrcSize.y - 1;

    /* Centre tap. */
    vec4 acc = uWeights[0] * texelFetch(uSrc, ivec2(xc, yc), 0);

    /* Symmetric pair loop: sample at xc±j for axis=0, yc±j for axis=1.
     * Loop bound is KMAX (compile-time const) — break when j > uRadius. */
    for (int j = 1; j <= ${KMAX}; j++) {
        if (j > uRadius) break;
        float w = uWeights[j];
        if (uAxis == 0) {
            int xL = mirrorIdx(xc - j, uSrcSize.x);
            int xR = mirrorIdx(xc + j, uSrcSize.x);
            acc += w * (texelFetch(uSrc, ivec2(xL, yc), 0)
                      + texelFetch(uSrc, ivec2(xR, yc), 0));
        } else {
            int yU = mirrorIdx(yc - j, uSrcSize.y);
            int yD = mirrorIdx(yc + j, uSrcSize.y);
            acc += w * (texelFetch(uSrc, ivec2(xc, yU), 0)
                      + texelFetch(uSrc, ivec2(xc, yD), 0));
        }
    }

    outColor = acc;
}
`;
